import { useCallback, useRef } from 'react';
import { AxiosError } from 'axios';
import { newIdempotencyKey, problemErrorCode } from './api';
import { isStepUpRefusal } from './step-up';

/**
 * Decide se o mesmo envio deve sair de novo com a mesma `Idempotency-Key` depois deste erro.
 *
 * A chave só protege a repetição se for reaproveitada (tarefa 4.2 da change
 * platform-backoffice-hardening): sem resposta (timeout, rede) o efeito pode ter saído, e a mesma
 * chave recebe o replay do 2xx ou o 409 `in_progress` enquanto o primeiro envio roda; no 5xx a API
 * guarda a falha, e a mesma chave recebe `previous_attempt_failed` em vez de executar de novo.
 * Nos demais (4xx de validação ou de teto, que liberam a chave, e os 409 de idempotência que já
 * mandam conferir o histórico) a próxima tentativa sai com chave nova.
 *
 * O 403 de step-up (`platform.step_up_required` e `platform.step_up_invalid`), o 429 do freio de
 * tentativas (`platform.step_up_rate_limited`) e o 423
 * `platform.auth.locked` também mantêm a chave: o filtro do step-up roda antes da reserva, então a
 * chave segue livre, e a repetição depois do modal de TOTP é o mesmo pedido (change
 * platform-backoffice-hardening, C6). Os demais 403 (papel sem permissão) descartam.
 */
export function keepsIdempotencyKey(err: unknown): boolean {
  if (!(err instanceof AxiosError)) return false;
  const status = err.response?.status;
  if (status === undefined) return true;
  if (status >= 500) return true;
  if (isStepUpRefusal(err)) return true;
  return status === 409 && problemErrorCode(err) === 'idempotency.in_progress';
}

/**
 * `Idempotency-Key` de um formulário de escrita comercial. A chave nasce com o pedido e é
 * reaproveitada enquanto o pedido for o mesmo (mesma rota e mesmo corpo): o operador que repete
 * depois de um timeout ou de um 5xx manda a mesma chave, e a API devolve o desfecho do primeiro
 * envio em vez de aplicar o efeito duas vezes. Muda quando o pedido muda (o operador alterou um
 * campo), depois do sucesso e depois de um erro que não pede a mesma chave (`keepsIdempotencyKey`).
 */
export function useIdempotencyKey() {
  const current = useRef<{ request: string; key: string } | null>(null);

  /** A chave do pedido: a mesma do envio anterior se o pedido não mudou e ela não foi descartada. */
  const keyFor = useCallback((url: string, body: unknown): string => {
    const request = JSON.stringify([url, body]);
    if (current.current?.request !== request) {
      current.current = { request, key: newIdempotencyKey() };
    }
    return current.current.key;
  }, []);

  /** Fecha o envio: sem erro (sucesso) descarta a chave; com erro, só a mantém se `keepsIdempotencyKey`. */
  const settle = useCallback((err?: unknown) => {
    if (err === undefined || !keepsIdempotencyKey(err)) current.current = null;
  }, []);

  return { keyFor, settle };
}
